import type Delta from "quill-delta";
import { MAX_SYMBOLS_IN_LINE, MIN_TEXT_WIDTH, SAFETY_PIXELS } from "./constantsV2";
import type { EditorCalc } from "./editorCalc";
import type { QuillInstance } from "./quillInstance";
import type { TextOverlay } from "./textOverlay";
import type { ResizeMode, ResizeOptions, ResizeResult, TextAlign, TextChangeMeta } from "./typesV2";
import { findBestStickerLayoutFromDelta } from "./utilsV2";

export class TextResizer {
    private committedWidth = 0;
    private committedHeight = 0;
    private options: {
        minWidth: number;
        paddingX: number;
        paddingY: number;
        safety: number;
        emptyWidth: number;
        mode: ResizeMode;

        fixedWidth?: number;
        fixedHeight?: number;
        currentFontSize?: number;
    };
    private hugeMeasureWidth = 6000;

    constructor(
        private quill: QuillInstance,
        private overlay: TextOverlay,
        private calc: EditorCalc,
        private onSize: ({ width, height, mode, fixedWidth }: ResizeResult) => void,
        private onFontSizeChange: (fontSize: number) => void,
        private getCurrentTextData: () => {
            fontFamily: string;
            fontSize: number;
            textAlign: TextAlign;
            delta: Delta;
        },
    ) {
        this.options = {
            safety: SAFETY_PIXELS,
            paddingX: 0,
            paddingY: 0,
            emptyWidth: 0,
            minWidth: MIN_TEXT_WIDTH,
            mode: "auto",
        };
    }

    onTextChange = (_delta: Delta, meta: TextChangeMeta) => {
        if (this.quill.getLength() <= 1) {
            this.handleEmptyState();
            return;
        }

        switch (this.options.mode) {
            case "auto":
                this.handleAuto(meta);
                return;

            case "shape":
                this.measureFixedHeight();
                return;

            case "sticker":
                this.measureAndFitSticker();
                return;
        }
    };

    setResizeOptions(newOptions: ResizeOptions) {
        this.options = { ...this.options, ...newOptions };
    }

    remeasure() {
        if (this.quill.getLength() <= 1) {
            this.handleEmptyState();
            return;
        }

        switch (this.options.mode) {
            case "auto": {
                const natural = this.measureNatural();
                const threshold = this.getAutoFixThreshold();

                if (natural.width + this.options.safety > threshold) {
                    this.applyShapeMeasurement(threshold);
                    return;
                }

                this.applyAutoMeasurement(false, natural);
                return;
            }

            case "shape":
                this.measureFixedHeight();
                return;

            case "sticker":
                this.measureAndFitSticker();
                return;
        }
    }

    clear() {
        this.options = {
            safety: SAFETY_PIXELS,
            paddingX: 0,
            paddingY: 0,
            emptyWidth: 0,
            minWidth: MIN_TEXT_WIDTH,
            mode: "auto",
        };

        this.committedWidth = 0;
        this.committedHeight = 0;
    }

    private handleEmptyState() {
        const width = this.options.mode === "auto" ? this.options.emptyWidth : this.getFixedWidth();

        const { height } = this.measureWithWidth(width);

        this.commitChanges(width, height);
    }

    private getFixedWidth() {
        return Math.max(
            this.options.minWidth,
            this.options.fixedWidth ?? this.committedWidth ?? this.options.minWidth,
        );
    }

    private getAutoFixThreshold() {
        //Примерно 85 символов в строке
        const widthMultiplicator = MAX_SYMBOLS_IN_LINE / 10;
        const autoFixThreshold = this.options.emptyWidth * widthMultiplicator;

        return Math.max(this.options.minWidth, autoFixThreshold);
    }

    private measureNatural() {
        const root = this.quill.getEditor() as HTMLElement;

        return this.calc.measure({
            html: root.innerHTML,
            styleSource: this.overlay.getStyleSourceEl(),
            fontFamily: this.overlay.getFontFamily(),
            minWidth: 0,
            maxWidth: Math.max(this.options.minWidth, this.hugeMeasureWidth),
            paddingX: this.options.paddingX,
            paddingY: this.options.paddingY,
        });
    }

    private measureWithWidth(width: number) {
        const root = this.quill.getEditor() as HTMLElement;

        return this.calc.measure({
            html: root.innerHTML,
            styleSource: this.overlay.getStyleSourceEl(),
            fontFamily: this.overlay.getFontFamily(),
            minWidth: width,
            maxWidth: width,
            paddingX: this.options.paddingX,
            paddingY: this.options.paddingY,
        });
    }

    private handleAuto(meta: TextChangeMeta) {
        if (meta.source !== "user") {
            this.applyAutoMeasurement(false);
            return;
        }

        const natural = this.measureNatural();
        const nextWidth = natural.width + this.options.safety;
        const threshold = this.getAutoFixThreshold();

        if (nextWidth > threshold) {
            this.applyShapeMeasurement(threshold);
            return;
        }

        const hasInsert = meta.insertedTextLength > 0;
        const hasDelete = meta.deletedLength > 0;
        const growOnly = hasInsert && !hasDelete;

        this.applyAutoMeasurement(growOnly, natural);
    }

    private applyAutoMeasurement(
        growOnly: boolean,
        measurement: { width: number; height: number } = this.measureNatural(),
    ) {
        const nextWidth = measurement.width + this.options.safety;

        const width = growOnly
            ? this.committedWidth === this.options.emptyWidth
                ? nextWidth
                : Math.max(this.committedWidth, nextWidth)
            : nextWidth;

        const height = growOnly
            ? Math.max(this.committedHeight, measurement.height)
            : measurement.height;

        this.commitChanges(width, height);
    }

    private applyShapeMeasurement(targetWidth: number) {
        const fixedWidth = Math.max(this.options.minWidth, targetWidth);
        const { height } = this.measureWithWidth(fixedWidth);

        this.options.mode = "shape";
        this.options.fixedWidth = fixedWidth;

        this.commitChanges(fixedWidth, height);
    }

    private measureFixedHeight() {
        const fixedWidth = this.getFixedWidth();
        const { height } = this.measureWithWidth(fixedWidth);

        this.commitChanges(fixedWidth, height);
    }

    private measureAndFitSticker() {
        if (!this.options.currentFontSize) {
            return;
        }

        const fixedWidth = this.getFixedWidth();
        const fixedHeight = this.options.fixedHeight ?? this.committedHeight;
        const currentTextData = this.getCurrentTextData();
        const bestLayout = findBestStickerLayoutFromDelta({
            data: {
                ...currentTextData,
                delta: this.quill.getDelta(),
            },
            hardWidth: fixedWidth,
            hardHeight: fixedHeight,
            maxFontSize: Math.max(1, Math.floor(this.options.currentFontSize)),
        });

        if (this.options.currentFontSize !== bestLayout.fontSize) {
            this.options.currentFontSize = bestLayout.fontSize;
            this.onFontSizeChange(bestLayout.fontSize);
        }

        this.commitChanges(fixedWidth, bestLayout.height);
    }

    private commitChanges(width: number, height: number) {
        if (this.committedWidth === width && this.committedHeight === height) {
            return;
        }

        this.committedWidth = width;
        this.committedHeight = height;

        this.onSize({
            width,
            height,
            mode: this.options.mode,
            fixedWidth: this.options.mode === "shape" ? this.getFixedWidth() : undefined,
        });
    }
}
